import { CommercialProposalStatus } from './status.js';
import type { CommercialProposalContent } from './content.js';
import type {
  CommercialProposalCalculationInput,
  CommercialProposalCalculationOutput,
} from './calculation.js';

// WHY: deterministic golden fixtures pin the canonical calculation formula
// (documented on `CommercialProposalCalculationOutputSchema`). The backend
// (#1195) executes the formula with Decimal + ROUND_HALF_UP; these fixtures
// are the contract the engine MUST reproduce. Expected money values are
// expressed as 2-decimal strings (NOT Decimal — `packages/shared` holds
// schemas + plain data only).

/**
 * @aiPurpose canonical "novat" golden fixture (#1194). Mirrors the
 *   prototype defaults. Verified to reproduce the documented totals.
 */
export const CP_GOLDEN_NOVAT_INPUT: CommercialProposalCalculationInput = {
  mode: 'BOTH',
  paymentScheme: 'novat',
  cnyRateRub: 12.3,
  usdRateRub: 83,
  goodsCny: 64800,
  chinaDeliveryCny: 0,
  servicePercent: 5,
  weightKg: 3000,
  volumeM3: 0,
  tariffUsdPerKg: 1.2,
  tariffUnit: 'kg',
  packagingUsd: 0,
  prrUsd: 150,
  insurancePercent: 1,
  noVatDivisor: 0.9,
  vatDivisor: 0.8,
  insuranceCnyPerUsdRate: 7.2,
};

/**
 * @aiPurpose expected output (2dp strings) for `CP_GOLDEN_NOVAT_INPUT`.
 * @aiInvariant `grandTotalRub` rounds the SUM of the unrounded row totals
 *   (buyRubFull 929880.00 + logisticsRubFull 354548.333…). For this
 *   fixture the rounded-row sum coincides with `grandTotalRub`; the
 *   reconciliation difference is exercised by `CP_RECONCILIATION_*`.
 */
export const CP_GOLDEN_NOVAT_OUTPUT = {
  paymentDivisor: '0.90',
  serviceCny: '3240.00',
  totalCny: '68040.00',
  buyRub: '929880.00',
  transportUsd: '3600.00',
  insuranceUsd: '94.50',
  logisticsUsd: '3844.50',
  logisticsRub: '354548.33',
  grandTotalRub: '1284428.33',
} as const;

/**
 * @aiPurpose reconciliation fixture (#1194) — proves the documented 0.01
 *   difference between `grandTotalRub` and the sum of the two ROUND_HALF_UP
 *   row totals. Inputs are constructed so each unrounded row total lands
 *   exactly on a half-cent boundary (10.005):
 *     buyRubFull       = 10.005  -> buyRub       = 10.01 (half-up)
 *     logisticsRubFull = 10.005  -> logisticsRub = 10.01 (half-up)
 *     buyRub + logisticsRub      = 20.02
 *     grandTotalRub = ROUND_HALF_UP(10.005 + 10.005) = ROUND_HALF_UP(20.01) = 20.01
 *   so grandTotalRub (20.01) differs from the summed rounded rows (20.02)
 *   by exactly 0.01 — the invariant that grand rounds the UNROUNDED sum.
 * @aiInvariant cash scheme (divisor 1), zero service / insurance /
 *   packaging so each row total is a single product.
 */
export const CP_RECONCILIATION_INPUT: CommercialProposalCalculationInput = {
  mode: 'BOTH',
  paymentScheme: 'cash',
  cnyRateRub: 1,
  usdRateRub: 1,
  goodsCny: 10.005,
  chinaDeliveryCny: 0,
  servicePercent: 0,
  weightKg: 10.005,
  volumeM3: 0,
  tariffUsdPerKg: 1,
  tariffUnit: 'kg',
  packagingUsd: 0,
  prrUsd: 0,
  insurancePercent: 0,
  noVatDivisor: 0.9,
  vatDivisor: 0.8,
  insuranceCnyPerUsdRate: 7.2,
};

export const CP_RECONCILIATION_OUTPUT = {
  paymentDivisor: '1.00',
  serviceCny: '0.00',
  totalCny: '10.01', // ROUND_HALF_UP(10.005) for the displayed 2dp total
  buyRub: '10.01',
  transportUsd: '10.01',
  insuranceUsd: '0.00',
  logisticsUsd: '10.01',
  logisticsRub: '10.01',
  grandTotalRub: '20.01',
} as const;

// The summed-rounded-rows value the reconciliation fixture must DIFFER from.
export const CP_RECONCILIATION_SUMMED_ROUNDED_ROWS = '20.02';

/**
 * @aiPurpose per-mode golden inputs (#1410) reusing the novat input shape, so
 *   the BUYOUT / LOGISTICS branches of the mode-gated engine are pinned. The
 *   only difference from `CP_GOLDEN_NOVAT_INPUT` is `mode`.
 */
export const CP_GOLDEN_BUYOUT_INPUT: CommercialProposalCalculationInput = {
  ...CP_GOLDEN_NOVAT_INPUT,
  mode: 'BUYOUT',
};

/**
 * @aiPurpose expected output for the BUYOUT-only novat input: the logistics
 *   rows collapse to "0.00"; the buyout rows match the BOTH output.
 */
export const CP_GOLDEN_BUYOUT_OUTPUT = {
  paymentDivisor: '0.90',
  serviceCny: '3240.00',
  totalCny: '68040.00',
  buyRub: '929880.00',
  transportUsd: '0.00',
  insuranceUsd: '0.00',
  logisticsUsd: '0.00',
  logisticsRub: '0.00',
  grandTotalRub: '929880.00',
} as const;

export const CP_GOLDEN_LOGISTICS_INPUT: CommercialProposalCalculationInput = {
  ...CP_GOLDEN_NOVAT_INPUT,
  mode: 'LOGISTICS',
};

/**
 * @aiPurpose expected output for the LOGISTICS-only novat input: no service
 *   markup, so `totalCny` collapses to goods only (64800) and the insurance
 *   basis is goods-only (BR-7); the buyout rows collapse to "0.00".
 *     insuranceUsd = 64800 / 7.2 * 1% = 90.00
 *     logisticsUsd = 3600 + 90 + 0 + 150 = 3840.00
 *     logisticsRub = 3840 * 83 / 0.9 = 354133.33…
 */
export const CP_GOLDEN_LOGISTICS_OUTPUT = {
  paymentDivisor: '0.90',
  serviceCny: '0.00',
  totalCny: '64800.00',
  buyRub: '0.00',
  transportUsd: '3600.00',
  insuranceUsd: '90.00',
  logisticsUsd: '3840.00',
  logisticsRub: '354133.33',
  grandTotalRub: '354133.33',
} as const;

/**
 * @aiPurpose plain content fixture covering every MVP field (#1194), used
 *   by the content round-trip test. Values mirror the prototype defaults.
 */
export const CP_SAMPLE_CONTENT: CommercialProposalContent = {
  mode: 'BOTH',
  proposalDate: '2026-05-29',
  client: 'ООО «Ромашка», Иван Петров',
  cnyRateRub: 12.3,
  usdRateRub: 83,
  buyoutTotalCurrency: 'RUB',
  logisticsTotalCurrency: 'RUB',
  grandTotalCurrency: 'RUB',
  productName: 'Сетка тканная из нержавеющей стали',
  characteristics: ['1*1мм ячейка', '0,32мм толщина', '1м ширина'],
  photoObjectKey:
    'request-item-photo/00000000-0000-0000-0000-000000000001/00000000-0000-0000-0000-000000000002',
  buyoutTerm: '3-4 рабочих дня',
  goodsCny: 64800,
  chinaDeliveryCny: 0,
  paymentScheme: 'novat',
  destination: 'Москва Южные ворота',
  logisticsTerm: '16-20 дней',
  weightKg: 3000,
  volumeM3: 2,
  tariffUsdPerKg: 1.2,
  tariffUnit: 'kg',
  packagingUsd: 0,
  prrUsd: 150,
  servicePercent: 5,
  noVatDivisor: 0.9,
  vatDivisor: 0.8,
  insurancePercent: 1,
  insuranceCnyPerUsdRate: 7.2,
  buyoutNotes:
    '§ Расчёт производится по курсу в день оплаты\n§ Стоимость доставки по Китаю может измениться',
  logisticsNotes:
    '§ Указанный вес и объём по данным поставщика\n§ Упаковка по согласованию с клиентом',
  managerName: 'Подольский Марк',
  managerPhone: '+7 (953) 115-33-30',
};

/** Shared non-RUB document fixture for web/PDF display-parity coverage (#2747). */
export const CP_NON_RUB_TOTALS_CONTENT: CommercialProposalContent = {
  ...CP_SAMPLE_CONTENT,
  buyoutTotalCurrency: 'CNY',
  logisticsTotalCurrency: 'USD',
  grandTotalCurrency: 'USD',
};

/** Status fixture for round-trip coverage. */
export const CP_SAMPLE_STATUS = CommercialProposalStatus.PUBLISHED;

/**
 * @aiPurpose m³-tariff golden (#1411): identical to the novat BOTH input
 *   except tariffUnit='m3' with a non-zero volumeM3 (50) DIFFERENT from
 *   weightKg (3000), so the freight multiplier provably switches to volume.
 *     transportUsd = volumeM3 * tariffUsdPerKg = 50 * 1.2 = 60.00
 *     (vs the kg golden's 3000 * 1.2 = 3600.00)
 *     logisticsUsd = 60 + 94.50 + 0 + 150 = 304.50
 *     logisticsRub = 304.50 * 83 / 0.9 = 28081.666… -> 28081.67
 *     grandTotalRub = ROUND_HALF_UP(929880 + 28081.666…) = 957961.67
 */
export const CP_GOLDEN_M3_INPUT: CommercialProposalCalculationInput = {
  ...CP_GOLDEN_NOVAT_INPUT,
  volumeM3: 50,
  tariffUnit: 'm3',
};

export const CP_GOLDEN_M3_OUTPUT: CommercialProposalCalculationOutput = {
  paymentDivisor: '0.90',
  serviceCny: '3240.00',
  totalCny: '68040.00',
  buyRub: '929880.00',
  transportUsd: '60.00',
  insuranceUsd: '94.50',
  logisticsUsd: '304.50',
  logisticsRub: '28081.67',
  grandTotalRub: '957961.67',
} as const;
